import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Database, Download, Upload, Trash2, RefreshCw, 
  AlertTriangle, CheckCircle, Loader2, FileJson,
  Newspaper, Building2, Users, Briefcase, ArrowLeft,
  Target, FileText, BarChart3, HardDrive, ArrowDownToLine, ArrowUpFromLine
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const countryFlags = {
  'SA': '🇸🇦',
  'AE': '🇦🇪',
  'QA': '🇶🇦',
  'BH': '🇧🇭',
  'KW': '🇰🇼',
  'OM': '🇴🇲'
};

export default function AdminDatabasePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingSection, setExportingSection] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [showFullImportDialog, setShowFullImportDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [collectionToClear, setCollectionToClear] = useState('');
  const [importResults, setImportResults] = useState(null);
  // WO-36 — whatever empties a whole collection must be confirmed by TYPING its
  // name (the server refuses it otherwise), and a full copy is saved first.
  const [confirmText, setConfirmText] = useState('');
  
  // Section-specific import state
  const [showSectionImportDialog, setShowSectionImportDialog] = useState(false);
  const [sectionToImport, setSectionToImport] = useState('');
  const [sectionImportMode, setSectionImportMode] = useState('merge');
  const [sectionImporting, setSectionImporting] = useState(false);
  const sectionFileInputRef = useRef(null);
  const fullImportFileRef = useRef(null);

  const token = localStorage.getItem('access_token');

  // News cleanup state
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupProgress, setCleanupProgress] = useState(null);
  const [cleanupDryRun, setCleanupDryRun] = useState(true);
  const cleanupAbortRef = useRef(null);

  // Bulk industry classification state
  const [classifyRunning, setClassifyRunning] = useState(false);
  const [classifyProgress, setClassifyProgress] = useState(null);
  const [classifyForce, setClassifyForce] = useState(false);
  const classifyAbortRef = useRef(null);

  // Bug cleanup state (DD-report data migrations)
  const [bugCleanupRunning, setBugCleanupRunning] = useState(false);
  const [bugCleanupResult, setBugCleanupResult] = useState(null);
  const [bugCleanupDryRun, setBugCleanupDryRun] = useState(true);
  
  // All sections configuration
  const sections = {
    news: {
      name: 'News',
      icon: Newspaper,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      gradient: 'from-red-500/20 to-red-600/10',
      uniqueField: 'title + country',
      collection: 'news'
    },
    tenders: {
      name: 'Tenders',
      icon: FileText,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
      borderColor: 'border-amber-500/30',
      gradient: 'from-amber-500/20 to-amber-600/10',
      uniqueField: 'title + country',
      collection: 'tenders'
    },
    projects: {
      name: 'Projects',
      icon: Building2,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      gradient: 'from-blue-500/20 to-blue-600/10',
      uniqueField: 'canonical_name + country',
      collection: 'projects'
    },
    companies: {
      name: 'Companies',
      icon: Briefcase,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      gradient: 'from-purple-500/20 to-purple-600/10',
      uniqueField: 'canonical_name',
      collection: 'companies'
    },
    opportunities: {
      name: 'Opportunities',
      icon: Target,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      gradient: 'from-green-500/20 to-green-600/10',
      uniqueField: 'name + tenant_id',
      // WO-36 — opportunities live in `opportunities` (import and export use
      // it); `leads` is a different, older collection, so this count was wrong.
      collection: 'opportunities'
    },
    contacts: {
      name: 'Contacts',
      icon: Users,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      gradient: 'from-orange-500/20 to-orange-600/10',
      uniqueField: 'email',
      collection: 'contacts'
    },
    reports: {
      name: 'Reports',
      icon: BarChart3,
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
      gradient: 'from-cyan-500/20 to-cyan-600/10',
      uniqueField: 'title',
      collection: 'reports'
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/database/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to fetch database statistics');
    } finally {
      setLoading(false);
    }
  };

  const runNewsCleanup = async () => {
    if (cleanupRunning) return;
    setCleanupRunning(true);
    setCleanupProgress({ processed: 0, total: 0, deleted: 0, kept: 0, enhanced: 0, errors: 0, progress: 0 });

    const controller = new AbortController();
    cleanupAbortRef.current = controller;

    try {
      const url = `${BACKEND_URL}/api/intelligence/news/cleanup-legacy-stream?dry_run=${cleanupDryRun}&batch_size=10`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Cleanup failed: HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.event === 'start') {
              setCleanupProgress(prev => ({ ...prev, total: data.total, dry_run: data.dry_run }));
            } else if (data.event === 'progress') {
              setCleanupProgress({ ...data });
            } else if (data.event === 'complete') {
              setCleanupProgress({ ...data });
              const msg = cleanupDryRun
                ? `Dry run complete — ${data.deleted} would be deleted, ${data.kept} kept, ${data.enhanced} would be enhanced.`
                : `Cleanup complete — ${data.deleted} deleted, ${data.kept} kept, ${data.enhanced} enhanced.`;
              toast.success(msg);
              if (!cleanupDryRun) fetchStats();
            } else if (data.event === 'error') {
              toast.error(`Cleanup error: ${data.message}`);
            }
          } catch (err) {
            // ignore malformed sse lines
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error(`Cleanup failed: ${err.message}`);
      }
    } finally {
      setCleanupRunning(false);
      cleanupAbortRef.current = null;
    }
  };

  const cancelNewsCleanup = () => {
    if (cleanupAbortRef.current) {
      cleanupAbortRef.current.abort();
      toast.info('Cleanup cancelled — progress so far is saved.');
    }
  };

  const runBulkClassify = async () => {
    if (classifyRunning) return;
    setClassifyRunning(true);
    setClassifyProgress({ processed: 0, total: 0, tagged: 0, errors: 0, progress: 0 });

    const controller = new AbortController();
    classifyAbortRef.current = controller;

    try {
      const url = `${BACKEND_URL}/api/intelligence/news/reclassify-industries-stream?batch_size=10&force=${classifyForce}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/event-stream' },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.event === 'start') {
              setClassifyProgress(prev => ({ ...prev, total: data.total }));
            } else if (data.event === 'progress') {
              setClassifyProgress({ ...data });
            } else if (data.event === 'complete') {
              setClassifyProgress({ ...data });
              toast.success(data.message || `Done — ${data.tagged} articles tagged across ${data.processed} processed.`);
            } else if (data.event === 'error') {
              toast.error(`Classification error: ${data.message}`);
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') toast.error(`Classification failed: ${err.message}`);
    } finally {
      setClassifyRunning(false);
      classifyAbortRef.current = null;
    }
  };

  const cancelBulkClassify = () => {
    if (classifyAbortRef.current) {
      classifyAbortRef.current.abort();
      toast.info('Classification cancelled — progress so far is saved.');
    }
  };


  const handleFullExport = async () => {
    try {
      setExporting(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/database/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mid2bid_full_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Full database exported successfully');
      } else {
        toast.error('Failed to export database');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error exporting database');
    } finally {
      setExporting(false);
    }
  };

  // WO-53 — customers' CRM (contacts, opportunities) is exported only with a
  // reason, optionally for one customer, and every export is in the audit log.
  const askCrmAccess = () => {
    const reason = (window.prompt(
      "Customers' CRM records. Why do you need them? Type: support, security or legal.\n" +
      "This export is recorded in the audit log.", 'support') || '').trim().toLowerCase();
    if (!reason) return null;
    if (!['support', 'security', 'legal'].includes(reason)) {
      toast.error('The reason must be support, security or legal.');
      return null;
    }
    const customer = (window.prompt(
      "The customer's account e-mail, to export only that customer's company.\n" +
      "Leave empty for all customers.", '') || '').trim();
    const note = (window.prompt('A short note for the log (for example the support request). Optional.', '') || '').trim();
    const h = { 'X-Access-Reason': reason };
    if (customer) h['X-Access-Customer'] = customer;
    if (note) h['X-Access-Note'] = note.slice(0, 200);
    return h;
  };

  const handleSectionExport = async (sectionKey) => {
    let accessHeaders = {};
    if (sectionKey === 'contacts' || sectionKey === 'opportunities') {
      const h = askCrmAccess();
      if (!h) return;
      accessHeaders = h;
    }
    try {
      setExportingSection(sectionKey);
      const response = await fetch(`${BACKEND_URL}/api/admin/database/export/${sectionKey}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...accessHeaders
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${sectionKey}_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`${sections[sectionKey]?.name || sectionKey} exported successfully`);
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.detail ? String(err.detail) : `Failed to export ${sectionKey}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Error exporting ${sectionKey}`);
    } finally {
      setExportingSection('');
    }
  };

  const handleFullImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON file');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${BACKEND_URL}/api/admin/database/import?mode=${importMode}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // WO-36 — the server has always required this for replace; the page
          // never sent it, so a full replace always failed.
          ...(importMode === 'replace' ? { 'X-Confirm-Drop': 'YES-I-UNDERSTAND' } : {})
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setImportResults(data);
        const refused = data?.results?.errors || [];
        if (refused.length) {
          toast.warning(`Imported, but ${refused.length} part(s) were refused: ${refused[0]}`);
        } else {
          toast.success('Full database imported successfully');
        }
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to import database');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Error importing database');
    } finally {
      setImporting(false);
      setShowFullImportDialog(false);
      event.target.value = '';
    }
  };

  const handleSectionImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON file');
      return;
    }
    
    try {
      setSectionImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${BACKEND_URL}/api/admin/database/import/${sectionToImport}?mode=${sectionImportMode}`, 
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(sectionImportMode === 'replace' ? { 'X-Confirm-Collection': sectionToImport } : {})
          },
          body: formData
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setImportResults({
          ...data.results,
          section: sectionToImport,
          collections: {
            [sectionToImport]: {
              imported: data.results.imported,
              updated: data.results.updated
            }
          },
          total_imported: data.results.imported,
          total_updated: data.results.updated
        });
        toast.success(`${sections[sectionToImport]?.name || sectionToImport} imported successfully`);
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to import ${sectionToImport}`);
      }
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(`Error importing ${sectionToImport}`);
    } finally {
      setSectionImporting(false);
      setShowSectionImportDialog(false);
      event.target.value = '';
    }
  };

  const openSectionImportDialog = (section) => {
    setSectionToImport(section);
    setSectionImportMode('merge');
    setConfirmText('');
    setShowSectionImportDialog(true);
  };

  const handleBugCleanup = async () => {
    setBugCleanupRunning(true);
    setBugCleanupResult(null);
    try {
      const url = `${BACKEND_URL}/api/admin/database/cleanup-bugs?dry_run=${bugCleanupDryRun}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Request failed: ${response.status}`);
      }
      const data = await response.json();
      setBugCleanupResult(data);
      toast.success(
        bugCleanupDryRun
          ? `Dry-run complete · would modify ${data.total_modified} rows`
          : `Cleanup complete · modified ${data.total_modified} rows`
      );
      // Refresh stats so users see the updated counts immediately
      fetchStats();
    } catch (err) {
      toast.error(`Bug cleanup failed: ${err.message}`);
    } finally {
      setBugCleanupRunning(false);
    }
  };

  const handleClearCollection = async () => {
    if (!collectionToClear) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/database/clear/${collectionToClear}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Confirm-Collection': confirmText.trim()
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(
          `Cleared ${data.deleted_count} documents from ${collectionToClear}` +
          (data.backup_collection ? ` — a copy is saved as ${data.backup_collection}` : '')
        );
        fetchStats();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to clear collection');
      }
    } catch (error) {
      toast.error('Error clearing collection');
    } finally {
      setShowClearDialog(false);
      setCollectionToClear('');
      setConfirmText('');
    }
  };

  const getCollectionCount = (sectionKey) => {
    if (!stats?.collections) return 0;
    const section = sections[sectionKey];
    if (!section) return 0;
    return stats.collections[section.collection] || 0;
  };

  const getTotalRecords = () => {
    if (!stats?.collections) return 0;
    return Object.values(stats.collections).reduce((a, b) => a + b, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10" data-testid="admin-database-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/dashboard')}
        className="mb-2"
        data-testid="back-to-dashboard-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>

      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20">
            <Database className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Database Management</h1>
            <p className="text-muted-foreground">Export, import, and manage your Mid2Bid database</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm py-1 px-3">
            <HardDrive className="w-4 h-4 mr-2" />
            {getTotalRecords().toLocaleString()} Total Records
          </Badge>
          <Button variant="outline" onClick={fetchStats} data-testid="refresh-stats-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {Object.entries(sections).map(([key, section]) => {
          const Icon = section.icon;
          const count = getCollectionCount(key);
          return (
            <Card key={key} className={`border ${section.borderColor} bg-gradient-to-br ${section.gradient}`}>
              <CardContent className="p-4 text-center">
                <Icon className={`w-6 h-6 mx-auto mb-2 ${section.color}`} />
                <div className="text-2xl font-bold">{count.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground">{section.name}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* News by Country */}
      {stats?.news_by_country && Object.keys(stats.news_by_country).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-red-500" />
              News Distribution by Country
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.news_by_country).map(([country, count]) => (
                <Badge key={country} variant="secondary" className="text-sm py-1 px-3">
                  {countryFlags[country]} {country}: {count.toLocaleString()}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="sections" className="w-full">
        <TabsList className="flex w-full overflow-x-auto justify-start md:grid md:grid-cols-5 mb-6">
          <TabsTrigger value="sections" className="flex items-center gap-2">
            <FileJson className="w-4 h-4" />
            Section Import/Export
          </TabsTrigger>
          <TabsTrigger value="full" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Full Database
          </TabsTrigger>
          <TabsTrigger value="news-cleanup" className="flex items-center gap-2" data-testid="news-cleanup-tab">
            <RefreshCw className="w-4 h-4" />
            News Cleanup
          </TabsTrigger>
          <TabsTrigger value="bug-cleanup" className="flex items-center gap-2" data-testid="bug-cleanup-tab">
            <CheckCircle className="w-4 h-4" />
            Bug Cleanup
          </TabsTrigger>
          <TabsTrigger value="danger" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Danger Zone
          </TabsTrigger>
        </TabsList>

        {/* Section Import/Export Tab */}
        <TabsContent value="sections" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileJson className="w-5 h-5 text-primary" />
                Individual Section Management
              </CardTitle>
              <CardDescription>
                Export or import data for specific sections. Each section can be managed independently.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {Object.entries(sections).map(([key, section]) => {
                  const Icon = section.icon;
                  const count = getCollectionCount(key);
                  const isExporting = exportingSection === key;
                  
                  return (
                    <Card key={key} className={`border-2 ${section.borderColor} hover:shadow-lg transition-all duration-200`}>
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-4">
                          <div className={`p-2 rounded-lg ${section.bgColor}`}>
                            <Icon className={`w-6 h-6 ${section.color}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{section.name}</h3>
                            <p className="text-sm text-muted-foreground">{count.toLocaleString()} records</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Button 
                            variant="outline" 
                            className="w-full justify-start"
                            onClick={() => handleSectionExport(key)}
                            disabled={isExporting || count === 0}
                            data-testid={`export-${key}-btn`}
                          >
                            {isExporting ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <ArrowDownToLine className="w-4 h-4 mr-2" />
                            )}
                            Export JSON
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            className="w-full justify-start"
                            onClick={() => openSectionImportDialog(key)}
                            data-testid={`import-${key}-btn`}
                          >
                            <ArrowUpFromLine className="w-4 h-4 mr-2" />
                            Import JSON
                          </Button>
                        </div>
                        
                        <p className="text-xs text-muted-foreground mt-3">
                          Merge key: <span className="font-mono">{section.uniqueField}</span>
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Full Database Tab */}
        <TabsContent value="full" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Full Export */}
            <Card className="border-2 border-primary/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-primary" />
                  Full Database Export
                </CardTitle>
                <CardDescription>
                  Download the complete database as a single JSON file for backup or transfer.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-muted/50 border">
                  <div className="flex items-start gap-3">
                    <BarChart3 className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium mb-1">Export includes:</p>
                      <p className="text-muted-foreground">
                        News, Tenders, Projects, Companies, Opportunities, Contacts, Reports, Users, and Tenants
                      </p>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={handleFullExport}
                  disabled={exporting}
                  className="w-full"
                  size="lg"
                  data-testid="export-full-database-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export Full Database
                </Button>
              </CardContent>
            </Card>

            {/* Full Import */}
            <Card className="border-2 border-accent/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-accent" />
                  Full Database Import
                </CardTitle>
                <CardDescription>
                  Upload a JSON file to import or merge database data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Import Mode</label>
                  <Select value={importMode} onValueChange={setImportMode}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="merge">Merge (Add new, update existing)</SelectItem>
                      <SelectItem value="replace">Replace (Clear all and import)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={() => setShowFullImportDialog(true)}
                  variant="outline"
                  className="w-full"
                  size="lg"
                  data-testid="import-full-database-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Database File
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Import Results */}
          {importResults && (
            <Card className="border-green-500/30 bg-green-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  Import Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{importResults.total_imported || 0}</div>
                    <div className="text-sm text-muted-foreground">Imported</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{importResults.total_updated || 0}</div>
                    <div className="text-sm text-muted-foreground">Updated</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-2xl font-bold text-amber-600">{importResults.skipped || 0}</div>
                    <div className="text-sm text-muted-foreground">Skipped</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{importResults.errors?.length || 0}</div>
                    <div className="text-sm text-muted-foreground">Errors</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Danger Zone Tab */}

        {/* News Cleanup Tab */}
        <TabsContent value="news-cleanup" className="space-y-4">
          <Card className="border-2 border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-primary" />
                Legacy News Cleanup
              </CardTitle>
              <CardDescription>
                Two-pass AI cleanup for articles already in the database:
                &nbsp;(1) re-classify by industry and <strong>delete irrelevant ones</strong>,
                &nbsp;(2) for the rest, generate English title + summary + CRM lead candidates if missing.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>How it works</AlertTitle>
                <AlertDescription className="space-y-2 mt-2 text-sm">
                  <p>Each article is classified against the pre-defined industries. Items classified as <em>irrelevant</em> or <em>unclassified</em> are deleted. Surviving items missing <code>title_en</code>, <code>summary_en</code> or <code>potential_leads</code> are re-enhanced with AI.</p>
                  <p>Use <strong>Dry Run</strong> first to see counts without touching the database. The real run is resumable — closing this tab just pauses progress; reopening and running again picks up where it left off.</p>
                  <p className="text-muted-foreground">Processing ~{stats?.collections?.news || '?'} articles at 10 in parallel, ~6-10 seconds per batch.</p>
                </AlertDescription>
              </Alert>

              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cleanup-dry-run"
                    checked={cleanupDryRun}
                    onChange={(e) => setCleanupDryRun(e.target.checked)}
                    disabled={cleanupRunning}
                    className="w-4 h-4"
                    data-testid="cleanup-dry-run-checkbox"
                  />
                  <label htmlFor="cleanup-dry-run" className="text-sm font-medium cursor-pointer">
                    Dry run (report only, no deletions or AI writes)
                  </label>
                </div>
              </div>

              {cleanupProgress && (
                <div className="space-y-3 p-4 bg-muted/20 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">
                      {cleanupRunning ? 'Running…' : 'Last run'}
                      {cleanupProgress.dry_run ? ' (dry run)' : ''}
                    </span>
                    <span className="text-muted-foreground">
                      {cleanupProgress.processed || 0} / {cleanupProgress.total || 0}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${cleanupProgress.progress || 0}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center text-sm pt-2">
                    <div className="p-2 bg-background rounded">
                      <div className="text-xl font-bold text-red-600">{cleanupProgress.deleted || 0}</div>
                      <div className="text-xs text-muted-foreground">Deleted</div>
                    </div>
                    <div className="p-2 bg-background rounded">
                      <div className="text-xl font-bold text-green-600">{cleanupProgress.kept || 0}</div>
                      <div className="text-xs text-muted-foreground">Kept</div>
                    </div>
                    <div className="p-2 bg-background rounded">
                      <div className="text-xl font-bold text-blue-600">{cleanupProgress.enhanced || 0}</div>
                      <div className="text-xs text-muted-foreground">Enhanced</div>
                    </div>
                    <div className="p-2 bg-background rounded">
                      <div className="text-xl font-bold text-amber-600">{cleanupProgress.errors || 0}</div>
                      <div className="text-xs text-muted-foreground">Errors</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {!cleanupRunning ? (
                  <Button
                    onClick={runNewsCleanup}
                    size="lg"
                    className="flex-1"
                    data-testid="cleanup-start-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {cleanupDryRun ? 'Start dry run' : 'Start real cleanup'}
                  </Button>
                ) : (
                  <Button
                    onClick={cancelNewsCleanup}
                    size="lg"
                    variant="destructive"
                    className="flex-1"
                    data-testid="cleanup-cancel-btn"
                  >
                    Cancel cleanup
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Bulk Industry Classification Card */}
          <Card className="border-2 border-blue-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-500" />
                Bulk Industry Classification
              </CardTitle>
              <CardDescription>
                Automatically classify existing news articles into one or more industry categories using OpenAI.
                Only processes articles that are missing the <code>industries</code> array (unless Force is enabled).
                Each article is classified from its AI Summary — no article is deleted.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-blue-500/30 bg-blue-500/5">
                <Target className="w-4 h-4 text-blue-500" />
                <AlertTitle>How it works</AlertTitle>
                <AlertDescription className="text-sm mt-1">
                  Each article is sent to OpenAI with its title and AI Summary. The model returns 1-3 industry tags
                  from the standard taxonomy (same as Tenders). Articles already tagged are skipped unless <strong>Force Re-classify</strong> is on.
                </AlertDescription>
              </Alert>

              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="classify-force"
                    checked={classifyForce}
                    onChange={(e) => setClassifyForce(e.target.checked)}
                    disabled={classifyRunning}
                    className="w-4 h-4"
                    data-testid="classify-force-checkbox"
                  />
                  <label htmlFor="classify-force" className="text-sm font-medium cursor-pointer">
                    Force re-classify all articles (including already-tagged ones)
                  </label>
                </div>
              </div>

              {classifyProgress && (
                <div className="space-y-3 p-4 bg-muted/20 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">
                      {classifyRunning ? 'Running…' : 'Last run'}
                    </span>
                    <span className="text-muted-foreground">
                      {classifyProgress.processed || 0} / {classifyProgress.total || 0}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${classifyProgress.progress || 0}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-sm pt-2">
                    <div className="p-2 bg-background rounded">
                      <div className="text-xl font-bold">{classifyProgress.processed || 0}</div>
                      <div className="text-xs text-muted-foreground">Processed</div>
                    </div>
                    <div className="p-2 bg-background rounded">
                      <div className="text-xl font-bold text-blue-600">{classifyProgress.tagged || 0}</div>
                      <div className="text-xs text-muted-foreground">Tagged</div>
                    </div>
                    <div className="p-2 bg-background rounded">
                      <div className="text-xl font-bold text-amber-600">{classifyProgress.errors || 0}</div>
                      <div className="text-xs text-muted-foreground">Errors</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {!classifyRunning ? (
                  <Button
                    onClick={runBulkClassify}
                    size="lg"
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="classify-start-btn"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    {classifyForce ? 'Re-classify all articles' : 'Classify untagged articles'}
                  </Button>
                ) : (
                  <Button
                    onClick={cancelBulkClassify}
                    size="lg"
                    variant="destructive"
                    className="flex-1"
                    data-testid="classify-cancel-btn"
                  >
                    Cancel classification
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bug-cleanup">
          <Card className="border-2 border-emerald-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="w-5 h-5" />
                Clean Up Bugs in Database
              </CardTitle>
              <CardDescription>
                Applies the data migrations that accompany the DD-report code fixes.
                Every task is idempotent — safe to re-run. Start with a dry-run to preview
                exactly what will change before committing.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="border-emerald-500/30 bg-emerald-500/5">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <AlertTitle>How it works</AlertTitle>
                <AlertDescription>
                  This endpoint always runs against the database this backend is connected to
                  (preview here, production after deploy). Each task reports scanned / modified
                  / skipped counts and is logged to audit_logs. Dry-run is the default — uncheck
                  it only when you are ready to commit the changes.
                </AlertDescription>
              </Alert>

              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/20">
                <input
                  id="bug-cleanup-dry-run"
                  type="checkbox"
                  checked={bugCleanupDryRun}
                  onChange={(e) => setBugCleanupDryRun(e.target.checked)}
                  disabled={bugCleanupRunning}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="bug-cleanup-dry-run"
                />
                <label htmlFor="bug-cleanup-dry-run" className="text-sm font-medium cursor-pointer select-none">
                  Dry run (preview changes without writing)
                </label>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleBugCleanup}
                  disabled={bugCleanupRunning}
                  className={bugCleanupDryRun
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                  data-testid="bug-cleanup-run-btn"
                >
                  {bugCleanupRunning ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                  ) : bugCleanupDryRun ? (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Preview clean up (dry run)</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" /> Clean up bugs in database</>
                  )}
                </Button>

                {bugCleanupResult && (
                  <Button
                    variant="outline"
                    onClick={() => setBugCleanupResult(null)}
                    data-testid="bug-cleanup-clear-btn"
                  >
                    Clear results
                  </Button>
                )}
              </div>

              {bugCleanupResult && (
                <div className="rounded-lg border" data-testid="bug-cleanup-results">
                  <div className="px-4 py-3 border-b bg-muted/30 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <Badge variant={bugCleanupResult.dry_run ? 'secondary' : 'default'}>
                        {bugCleanupResult.dry_run ? 'DRY RUN' : 'COMMITTED'}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {bugCleanupResult.duration_ms} ms · {bugCleanupResult.tasks_run.length} tasks
                      </span>
                    </div>
                    <span className="text-sm font-semibold">
                      Total {bugCleanupResult.dry_run ? 'would-modify' : 'modified'}:{' '}
                      <span className="text-emerald-600">{bugCleanupResult.total_modified}</span>
                    </span>
                  </div>
                  <div className="divide-y">
                    {Object.entries(bugCleanupResult.results || {}).map(([key, r]) => (
                      <div key={key} className="px-4 py-3" data-testid={`bug-cleanup-task-${key}`}>
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <div className="font-medium text-sm">{r.label}</div>
                          <Badge
                            variant="outline"
                            className={r.status === 'ok'
                              ? 'border-emerald-500/50 text-emerald-600'
                              : 'border-red-500/50 text-red-600'}
                          >
                            {r.status === 'ok' ? 'OK' : 'ERROR'}
                          </Badge>
                        </div>
                        {r.status === 'ok' ? (
                          <div className="text-xs text-muted-foreground flex flex-wrap gap-4">
                            <span>scanned: <strong>{r.scanned}</strong></span>
                            <span>modified: <strong className="text-emerald-600">{r.modified}</strong></span>
                            <span>skipped: <strong>{r.skipped}</strong></span>
                            {typeof r.collisions === 'number' && r.collisions > 0 && (
                              <span className="text-amber-600">
                                collisions: <strong>{r.collisions}</strong>
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-red-600 font-mono">{r.error}</div>
                        )}
                        {Array.isArray(r.examples) && r.examples.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-primary cursor-pointer">
                              Show {r.examples.length} example{r.examples.length > 1 ? 's' : ''}
                            </summary>
                            <pre className="mt-1 text-[11px] bg-muted/30 p-2 rounded overflow-x-auto">
                              {JSON.stringify(r.examples, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="danger">
          <Card className="border-2 border-destructive/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>
                Destructive actions that cannot be undone. Use with extreme caution!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive" className="mb-6">
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>Warning</AlertTitle>
                <AlertDescription>
                  Clearing a collection deletes all of its records. A full copy is saved first, inside the database,
                  as &lt;collection&gt;_backup_&lt;date and time&gt;, and you must type the collection's name to confirm.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {['news', 'tenders', 'projects', 'companies', 'leads'].map(collection => (
                  <Button
                    key={collection}
                    variant="outline"
                    className="border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => {
                      setCollectionToClear(collection);
                      setConfirmText('');
                      setShowClearDialog(true);
                    }}
                    data-testid={`clear-${collection}-btn`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear {collection}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Full Import Dialog */}
      <Dialog open={showFullImportDialog} onOpenChange={setShowFullImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Full Database</DialogTitle>
            <DialogDescription>
              Upload a JSON file to import. Mode: {importMode === 'merge' ? 'Merge' : 'Replace'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {importMode === 'replace' && (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>Warning</AlertTitle>
                <AlertDescription>
                  Replace mode empties each collection in the file before importing. The current data is kept as a
                  backup collection. Accounts, companies, the audit trail, keys, payments and referrals are never
                  imported.
                </AlertDescription>
              </Alert>
            )}
            
            <input
              ref={fullImportFileRef}
              type="file"
              accept=".json"
              onChange={handleFullImport}
              disabled={importing}
              className="w-full file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
            />
            
            {importing && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Importing...
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFullImportDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Section Import Dialog */}
      <Dialog open={showSectionImportDialog} onOpenChange={setShowSectionImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {sections[sectionToImport] && (
                <>
                  {React.createElement(sections[sectionToImport].icon, {
                    className: `w-5 h-5 ${sections[sectionToImport].color}`
                  })}
                  Import {sections[sectionToImport]?.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Upload a JSON file to import {sections[sectionToImport]?.name?.toLowerCase() || 'data'}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Import Mode Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Import Mode</label>
              <Select value={sectionImportMode} onValueChange={setSectionImportMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">
                    <div className="flex flex-col">
                      <span className="font-medium">Merge</span>
                      <span className="text-xs text-muted-foreground">Add new records, update existing</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="replace">
                    <div className="flex flex-col">
                      <span className="font-medium">Replace</span>
                      <span className="text-xs text-muted-foreground">Clear all and import fresh</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Mode Description */}
            {sectionImportMode === 'merge' ? (
              <Alert>
                <CheckCircle className="w-4 h-4 text-green-500" />
                <AlertTitle>Merge Mode</AlertTitle>
                <AlertDescription>
                  <ul className="text-sm mt-1 space-y-1">
                    <li>• New records will be added</li>
                    <li>• Existing records will be updated</li>
                    <li>• Matched by: <strong>{sections[sectionToImport]?.uniqueField}</strong></li>
                  </ul>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertTriangle className="w-4 h-4" />
                <AlertTitle>Replace Mode</AlertTitle>
                <AlertDescription>
                  <ul className="text-sm mt-1 space-y-1">
                    <li>• All existing {sections[sectionToImport]?.name?.toLowerCase()} will be DELETED — for every company</li>
                    <li>• Only imported records will remain</li>
                    <li>• A full copy is saved first as a backup collection</li>
                  </ul>
                  <div className="mt-3 space-y-1">
                    <label className="text-xs font-medium">Type <b>{sectionToImport}</b> to allow this</label>
                    <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                      placeholder={sectionToImport} data-testid="section-replace-confirm" />
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            {/* File Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select JSON File</label>
              <input
                ref={sectionFileInputRef}
                type="file"
                accept=".json"
                onChange={handleSectionImport}
                disabled={sectionImporting || (sectionImportMode === 'replace' && confirmText.trim() !== sectionToImport)}
                className="w-full file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                data-testid="section-import-file-input"
              />
            </div>
            
            {sectionImporting && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Importing {sections[sectionToImport]?.name}...
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSectionImportDialog(false)}
              disabled={sectionImporting}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Confirmation Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">
              Clear {collectionToClear}?
            </DialogTitle>
            <DialogDescription>
              This deletes ALL documents in the {collectionToClear} collection. A full copy is saved first as a
              backup collection.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <label className="text-sm font-medium">Type <b>{collectionToClear}</b> to confirm</label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
              placeholder={collectionToClear} data-testid="clear-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleClearCollection}
              disabled={confirmText.trim() !== collectionToClear} data-testid="clear-confirm-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear {collectionToClear}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
